import React from 'react';
import styled from 'styled-components';
import { trackAnalytics, attachLocale, toSlug } from '../../../../lib/utils';
import { DOM_GREETING_SECTION } from '../../../../lib/constants';
import GreetingSectionShops from './GreetingSectionShops';
import MediaQueryHelper from '../../../templates/MediaQueryHelper';
import {
  GenericGreetingContainerDiv,
  GreetingSectionHeaderDiv,
  GreetingSectionSubHeaderDiv,
  GreetingCenteredSectionWithTopMargin,
} from './GreetingSectionSharedStyles';
import Button from '../Button/Button';

const CARD_WIDTH = 238;
const TABLET_CARD_WIDTH = 212;
const CARDS_GAP = 16;

const getTabletCardWidth = (cardsCount) => {
  return cardsCount >= 3
    ? TABLET_CARD_WIDTH
    : CARD_WIDTH;
};

const GreetingSectionWithFavContainer = styled(GenericGreetingContainerDiv)`
  display: flex;
  justify-content: space-between;
  align-items: center;

  .mobileCta {
    display: none;
  }

  @media screen and (max-width: ${(p) => p.theme.breakpoint.tablet}px) {
    flex-direction: column;
    justify-content: center;

    .desktopCta {
      display: none;
    }

    .mobileCta {
      display: block;
    }
  }
`;

const SwiperContainer = styled.div`
  width: 768px;
  position: relative;

  @media screen and (max-width: ${(p) => p.theme.breakpoint.desktop}px) {
    width: 760px;
  }

  .shop-card__wrapper {
    width: ${CARD_WIDTH}px;

    @media screen and (max-width: ${(p) => p.theme.breakpoint.desktop}px) {
      ${({ savedShopsCount }) => (
        `width: ${getTabletCardWidth(savedShopsCount)}px;`
      )}
    }

    @media screen and (max-width: ${(p) => p.theme.breakpoint.mobile - 1}px) {
      width: ${CARD_WIDTH}px;
    }
  }

  .swiper-slide {
    @media screen and (max-width: ${(p) => p.theme.breakpoint.desktop}px) {
      ${({ savedShopsCount }) => (
        `width: ${getTabletCardWidth(savedShopsCount) + 4}px !important;`
      )}
    }
  }

  ${({ savedShopsCount }) => {
    if (savedShopsCount <= 3) {
      return `
        margin-top: 28px;

        .swiper-pagination {
          display: none;
        }

        .swiper-slide {
          padding-top: 4px;
        }
      `;
    }
  }}

  @media screen and (max-width: ${(p) => p.theme.breakpoint.tablet}px) {
    .ams-swiper__container {
      margin: 0;
    }

    ${({ savedShopsCount }) => {
    switch (savedShopsCount) {
      case 1: {
        return `
          width: ${getTabletCardWidth(savedShopsCount) + 4}px;
          align-self: center;
        `;
      }

      case 2: {
        return `
          width: ${(getTabletCardWidth(savedShopsCount) + 4) * 2 + CARDS_GAP}px;
          align-self: center;
        `;
      }

      case 3:
      default: {
        return `width: ${(getTabletCardWidth(savedShopsCount) + 4) * 3 + CARDS_GAP * 2}px;`;
      }
    }
  }}
  }

  @media screen and (max-width: ${(p) => p.theme.breakpoint.mobile - 1}px) {   
    margin-top: 20px;

    ${({ savedShopsCount }) => {
    if (savedShopsCount === 1) {
      return `
        width: ${CARD_WIDTH + 16}px;
        align-self: center;
      `;
    }

    return `
      width: 100vw;
      align-self: center;
      overflow: auto;
      
      > div {
        padding-left: 7px;
      }
    `;
  }}
  }
`;

const MOBILE_SHOPS_GRID_STYLE = {
  'flex-flow': 'row nowrap',
  'justify-content': 'flex-start',
};

const GreetingSectionInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex-shrink: 0;

  max-width: 356px;

  > div {
    text-align: left;
  }

  @media screen and (max-width: ${(p) => p.theme.breakpoint.tablet}px) {
    align-items: center;

    > div {
      text-align: center;
    }
  }
`;

const GreetingSectionWithFavs = (props) => {
  const {
    pageStateModifiers,
    userShopsData,
    locale,
    userLoggedInWithFavouriteShopsLineOne,
    userLoggedInWithFavouriteShopsLineTwo,
    userLoggedInWithFavouriteShopsCtaText,
    userLoggedInWithFavouriteShopsCtaUrl,
    userLoggedInWithFavouriteShopsCtaTagSet,
    firstName,
  } = props;

  const resolvedCtaUrl = attachLocale(userLoggedInWithFavouriteShopsCtaUrl, locale);

  const ctaTrack = trackAnalytics(
    `${DOM_GREETING_SECTION}-with-favs`,
    `${toSlug(userLoggedInWithFavouriteShopsCtaText)}`,
    resolvedCtaUrl,
    userLoggedInWithFavouriteShopsCtaTagSet
  );

  const renderPage = (isMobile) => {
    return (
      <GreetingSectionWithFavContainer>
        <GreetingSectionInfoWrapper>
          <GreetingSectionHeaderDiv className="fs-mask" isMobile={isMobile}>
            { userLoggedInWithFavouriteShopsLineOne }
            <br />
            { firstName }
          </GreetingSectionHeaderDiv>

          <GreetingSectionSubHeaderDiv isMobile={isMobile}>
            { userLoggedInWithFavouriteShopsLineTwo }
          </GreetingSectionSubHeaderDiv>

          <GreetingCenteredSectionWithTopMargin top={32} className="desktopCta">
            <Button
              variant="primary"
              size="medium"
              buttonText={userLoggedInWithFavouriteShopsCtaText}
              {...ctaTrack}
            />
          </GreetingCenteredSectionWithTopMargin>
        </GreetingSectionInfoWrapper>

        <SwiperContainer savedShopsCount={userShopsData.length || 0}>
          <GreetingSectionShops
            pageStateModifiers={pageStateModifiers}
            userShopsData={userShopsData}
            locale={locale}
            mobileGridStyle={MOBILE_SHOPS_GRID_STYLE}
          />
        </SwiperContainer>

        <GreetingCenteredSectionWithTopMargin top={32} className="mobileCta">
          <Button
            variant="primary"
            size="medium"
            buttonText={userLoggedInWithFavouriteShopsCtaText}
            {...ctaTrack}
          />
        </GreetingCenteredSectionWithTopMargin>
      </GreetingSectionWithFavContainer>
    );
  };

  return (
    <MediaQueryHelper
      desktopChildren={renderPage()}
      mobileChildren={renderPage(true)}
    />
  );
};

export default GreetingSectionWithFavs;
